import React, { useState, useEffect, useRef, useCallback } from 'react';
import { api, getText } from '../api';
import { useToast } from '../ToastContext';
import { csvToObjects, downloadText } from '../csvUtil';

export function CarPicker({ existingCarIds, locationId, onAddExisting, onAddNew, onClose }) {
  const [query, setQuery]     = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importErrors, setImportErrors] = useState([]);
  const inputRef = useRef(null);
  const importInputRef = useRef(null);
  const toast = useToast();

  useEffect(() => { inputRef.current?.focus(); }, []);

  const search = useCallback(async (q) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q.trim()) params.set('search', q.trim());
      if (locationId) params.set('location_id', locationId);
      setResults(await api.get(`/cars${params.toString() ? `?${params}` : ''}`));
    } finally {
      setLoading(false);
    }
  }, [locationId]);

  useEffect(() => { search(''); }, [search]);
  useEffect(() => {
    const t = setTimeout(() => search(query), 300);
    return () => clearTimeout(t);
  }, [query, search]);

  const deleteCar = async (e, car) => {
    e.stopPropagation();
    if (!window.confirm(`Delete ${[car.year, car.make, car.model].filter(Boolean).join(' ') || 'this car'} from inventory? This cannot be undone.`)) return;
    setDeleting(car.id);
    try {
      await api.del(`/cars/${car.id}`);
      setResults(r => r.filter(c => c.id !== car.id));
    } catch (err) {
      toast.error(err.message);
    } finally {
      setDeleting(null);
    }
  };

  const exportCsv = async () => {
    try {
      // Always the caller's own lot — same scoping as everything else in cars.js.
      downloadText('inventory.csv', await getText('/cars/export'));
    } catch (err) {
      toast.error(err.message);
    }
  };

  const importCsv = async (e) => {
    const file = e.target.files[0];
    e.target.value = '';
    if (!file) return;
    const rows = csvToObjects(await file.text());
    if (!rows.length) return toast.error('That file has no data rows.');
    setImporting(true);
    setImportErrors([]);
    try {
      const result = await api.post('/cars/import', { rows });
      if (result.created.length) toast.success(`Imported ${result.created.length} car${result.created.length === 1 ? '' : 's'}.`);
      if (result.errors.length) setImportErrors(result.errors);
      search(query);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl w-full max-w-lg flex flex-col max-h-[85dvh]">
        <div className="p-5 border-b border-slate-100 dark:border-slate-700">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-base font-bold text-slate-900 dark:text-white">Add Car to Deal</h2>
            <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <div className="relative">
            <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input ref={inputRef} type="text" placeholder="Search by VIN, stock #, make, or model…"
              value={query} onChange={e => setQuery(e.target.value)} className="input pl-9" />
          </div>
        </div>

        {importErrors.length > 0 && (
          <div className="mx-4 mt-3 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 text-xs space-y-1 max-h-32 overflow-y-auto">
            <div className="flex items-center justify-between font-semibold">
              <span>{importErrors.length} row{importErrors.length === 1 ? '' : 's'} failed to import</span>
              <button onClick={() => setImportErrors([])} className="opacity-60 hover:opacity-100">Dismiss</button>
            </div>
            {importErrors.map((e, i) => <p key={i}>Row {e.row}: {e.error}</p>)}
          </div>
        )}

        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-10">
              <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : results.length === 0 ? (
            <p className="text-sm text-slate-400 dark:text-slate-500 text-center py-10">
              {query ? 'No cars match your search.' : 'No cars in inventory yet.'}
            </p>
          ) : (
            <div className="p-2 space-y-1">
              {results.map(car => {
                const already = existingCarIds.has(car.id);
                return (
                  <div key={car.id} className={`flex items-center gap-3 p-3 rounded-xl ${already ? 'opacity-50' : 'hover:bg-slate-50 dark:hover:bg-slate-700'} transition-colors`}>
                    <div className="w-14 h-10 bg-slate-100 rounded-lg shrink-0 flex items-center justify-center overflow-hidden">
                      {car.photos_videos?.[0]
                        ? <img src={car.photos_videos[0]} alt="" className="w-full h-full object-cover rounded-lg" />
                        : <svg className="w-6 h-4 text-slate-300" viewBox="0 0 60 40" fill="currentColor"><path d="M8 28 Q12 14 20 12 L40 12 Q48 14 52 28 L54 34 Q54 37 51 37 L9 37 Q6 37 6 34 Z" opacity="0.5"/><circle cx="17" cy="37" r="5"/><circle cx="43" cy="37" r="5"/></svg>}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-slate-800 truncate">
                        {[car.year, car.make, car.model, car.trim].filter(Boolean).join(' ') || 'Unknown Vehicle'}
                      </p>
                      <p className="text-xs text-slate-500 truncate">
                        {car.vin ? `VIN: ${car.vin}` : 'No VIN'}
                        {car.mileage ? ` · ${Number(car.mileage).toLocaleString()} mi` : ''}
                        {car.stock_number ? ` · ${car.stock_number}` : ''}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {already
                        ? <span className="text-xs font-semibold text-slate-400 bg-slate-100 dark:bg-slate-700 px-2.5 py-1 rounded-full">In deal</span>
                        : <button onClick={() => onAddExisting(car)} className="btn-primary text-xs px-3 py-1.5">Add</button>}
                      <button
                        onClick={(e) => deleteCar(e, car)}
                        disabled={deleting === car.id}
                        title="Delete from inventory"
                        className="w-7 h-7 flex items-center justify-center rounded text-slate-300 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors disabled:opacity-40"
                      >
                        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="p-4 border-t border-slate-100 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50 rounded-b-2xl space-y-2">
          <button onClick={onAddNew} className="w-full btn-secondary flex items-center justify-center gap-2">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Create New Car
          </button>
          <div className="flex items-center gap-2">
            <input ref={importInputRef} type="file" accept=".csv,text/csv" className="hidden" onChange={importCsv} />
            <button
              onClick={() => importInputRef.current?.click()}
              disabled={importing}
              className="flex-1 btn-secondary text-xs py-2 disabled:opacity-50"
            >
              {importing ? 'Importing…' : 'Import CSV'}
            </button>
            <button onClick={exportCsv} className="flex-1 btn-secondary text-xs py-2">
              Export CSV
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
